import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";

import { RoleProtectedRoute } from "../ProtectedRoute";
import { useApi } from "../../api";
import useI18nSnackbar from "../../hooks/useI18nSnackbar";
import Table from "./Table";
import List from "./List";
import Pagination from "../../elements/Pagination";
import SearchTermFilter from "./Search/SearchTermFilter";
import { useWhitelabel } from "../../domain/whitelabel";
import Export from "../Export";
import ConfigDialog from "./Config/ConfigDialog";
import BlockQueries from "../BlockQueries";
import RefDateQueries from "../RefDateQueries";
import Create from "../Devices/Create";
import PageSelect from "../../elements/PageSelect";
import { ROLES } from "../../auth";
import { useDeviceIds } from "../../domain/device";
import EmptyState from "./EmptyState";

// Keeps filters (and the current page) restorable across navigation: the
// URL is the persisted source of truth, this just reads it back out.
const parseDeviceFiltersFromQuery = search => {
  const params = new URLSearchParams(search);
  const splitParam = value => (value ? value.split("|") : []);

  return {
    searchTerm: params.get("search_term") || "",
    typeFilter: params.get("type") || "",
    contentTypeFilter: params.get("sensor_content_type") || "",
    projectFilter: splitParam(params.get("project")),
    ownerFilter: splitParam(params.get("owner_id")).map(Number),
    statusFilter: splitParam(params.get("status")),
    page: Number(params.get("page")) || 1
  };
};

const ListContainer = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const {
    whitelabelConfig,
    whitelabelConfigLoaded,
    updateWhitelabelConfig
  } = useWhitelabel();
  const { reload: reloadDeviceIds } = useDeviceIds();
  const { enqueueSnackbar } = useI18nSnackbar();

  const initialFilters = parseDeviceFiltersFromQuery(location.search);

  const [devices, setDevices] = useState([]);
  const [sort, setSort] = useState({ by: null, desc: false });
  const [totalCount, setTotalCount] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);
  const [typeFilter, setTypeFilter] = useState(initialFilters.typeFilter);
  const [contentTypeFilter, setContentTypeFilter] = useState(
    initialFilters.contentTypeFilter
  );
  const [projectFilter, setProjectFilter] = useState(
    initialFilters.projectFilter
  );
  const [ownerFilter, setOwnerFilter] = useState(initialFilters.ownerFilter);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [tableColumns, setTableColumns] = useState([]);

  const [sensorSettings, setSensorSettings] = useState({
    showNames: false,
    showVisuals: false,
    showUnits: false,
    showPercentages: false,
    showTemperatures: false
  });

  const [page, setPage] = useState(initialFilters.page);
  const [perPage, setPerPage] = useState(25);
  const [readyToLoadDevices, setReadyToLoadDevices] = useState(false);

  const [isLoading, setLoading] = useState(true);
  const [isUpdating, setUpdating] = useState(false);

  const groupByUser = whitelabelConfig["ui.devices.groupByUser"];

  const handleSetSearchTerm = useCallback(term => {
    setSearchTerm(prev => {
      if (prev === term) return prev;
      setPage(1);
      return term;
    });
  }, []);

  const handleSetTypeFilter = useCallback(type => {
    setTypeFilter(prev => {
      if (prev === type) return prev;
      setPage(1);
      return type;
    });
  }, []);

  const handleSetContentTypeFilter = useCallback(type => {
    setContentTypeFilter(prev => {
      if (prev === type) return prev;
      setPage(1);
      return type;
    });
  }, []);

  const handleSetProjectFilter = useCallback(values => {
    setProjectFilter(prev => {
      if (prev.join("|") === values.join("|")) return prev;
      setPage(1);
      return values;
    });
  }, []);

  const handleSetOwnerFilter = useCallback(values => {
    setOwnerFilter(prev => {
      if (prev.join("|") === values.join("|")) return prev;
      setPage(1);
      return values;
    });
  }, []);

  const handleSetStatusFilter = useCallback(values => {
    setStatusFilter(prev => {
      if (prev.join("|") === values.join("|")) return prev;
      setPage(1);
      return values;
    });
  }, []);

  const handleSelectPerPage = useCallback(
    perPageValue => {
      const value = parseInt(perPageValue, 10);
      updateWhitelabelConfig("ui.devices.list.perPage", value);
      setPerPage(value);
      setPage(1);
    },
    [updateWhitelabelConfig]
  );

  const handleShowPercentages = useCallback(
    value => {
      updateWhitelabelConfig("ui.devices.list.sensors.showPercentages", value);

      setSensorSettings(sensorSettings => ({
        ...sensorSettings,
        showPercentages: value
      }));
    },
    [updateWhitelabelConfig]
  );

  const handleUpdateSort = useCallback(
    by => {
      setSort(prevSort => {
        let desc = false;
        if (prevSort.by === by && prevSort.desc === false) {
          desc = true;
        }

        updateWhitelabelConfig("ui.devices.sort.by", by);
        updateWhitelabelConfig("ui.devices.sort.desc", desc);
        reloadDeviceIds();

        return { by, desc };
      });
    },
    [updateWhitelabelConfig, reloadDeviceIds]
  );

  // prevent sort state from being initialized multiple times
  const sortInitialized = useRef(false);

  // only reset to page 1 when the user actually toggles grouping, not on the
  // initial mount, otherwise a page restored from the URL gets clobbered
  const groupByUserInitialized = useRef(false);

  useEffect(() => {
    if (!groupByUserInitialized.current) {
      groupByUserInitialized.current = true;
      return;
    }
    setPage(1);
  }, [groupByUser]);

  useEffect(() => {
    if (!whitelabelConfigLoaded) return;

    // Only initialize sort once
    if (!sortInitialized.current) {
      setSort({
        by: whitelabelConfig["ui.devices.sort.by"],
        desc: whitelabelConfig["ui.devices.sort.desc"]
      });
      sortInitialized.current = true;
    }

    setPerPage(whitelabelConfig["ui.devices.list.perPage"]);

    setTableColumns(prev => {
      const newColumns = whitelabelConfig["ui.devices.list.columns"] || [];
      if (JSON.stringify(prev) === JSON.stringify(newColumns)) {
        return prev;
      }
      return newColumns;
    });

    // only update if changed
    setSensorSettings(prev => {
      const newSettings = {
        showNames: whitelabelConfig["ui.devices.list.sensors.showNames"],
        showVisuals: whitelabelConfig["ui.devices.list.sensors.showVisuals"],
        showUnits: whitelabelConfig["ui.devices.list.sensors.showUnits"],
        showPercentages:
          whitelabelConfig["ui.devices.list.sensors.showPercentages"],
        showTemperatures:
          whitelabelConfig["ui.devices.list.sensors.showTemperatures"]
      };

      // Deep comparison
      const hasChanged = Object.keys(newSettings).some(
        key => prev[key] !== newSettings[key]
      );

      return hasChanged ? newSettings : prev;
    });

    setReadyToLoadDevices(true);
  }, [whitelabelConfig, whitelabelConfigLoaded]);

  // prevent unneeded API calls
  const queryParams = useMemo(() => {
    const params = {};

    if (sort.by) {
      params.sort = `${sort.by}:${sort.desc ? "desc" : "asc"}`;
    }

    if (page) {
      params.page = page;
    }

    if (perPage) {
      params.per_page = perPage;
    }

    if (searchTerm) {
      params.search_term = searchTerm;
    }

    if (typeFilter) {
      params.type = typeFilter;
    }
    if (contentTypeFilter) {
      params.sensor_content_type = contentTypeFilter;
    }

    if (projectFilter.length > 0) {
      params.project = projectFilter.join("|");
    }
    if (ownerFilter.length > 0) {
      params.owner_id = ownerFilter.join("|");
    }
    if (statusFilter.length > 0) {
      params.status = statusFilter.join("|");
    }

    if (groupByUser) {
      params.grouped = 1;
    }

    return params;
  }, [
    sort.by,
    sort.desc,
    page,
    perPage,
    searchTerm,
    typeFilter,
    contentTypeFilter,
    projectFilter,
    ownerFilter,
    statusFilter,
    groupByUser
  ]);

  // mirror filters + page into the URL so they survive navigating away and back
  const urlFilterParams = useMemo(() => {
    const params = {};

    if (searchTerm) params.search_term = searchTerm;
    if (typeFilter) params.type = typeFilter;
    if (contentTypeFilter) params.sensor_content_type = contentTypeFilter;
    if (projectFilter.length > 0) params.project = projectFilter.join("|");
    if (ownerFilter.length > 0) params.owner_id = ownerFilter.join("|");
    if (statusFilter.length > 0) params.status = statusFilter.join("|");
    if (page > 1) params.page = page;

    return params;
  }, [
    searchTerm,
    typeFilter,
    contentTypeFilter,
    projectFilter,
    ownerFilter,
    statusFilter,
    page
  ]);

  useEffect(() => {
    const search = new URLSearchParams(urlFilterParams).toString();

    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : ""
    });
  }, [urlFilterParams, location.pathname, history]);

  useEffect(() => {
    if (!readyToLoadDevices) return;

    setUpdating(true);

    api
      .get("/v2/devices", { params: queryParams })
      .then(response => {
        setDevices(response.data);
        setTotalCount(response.headers["x-total-count"]);
      })
      .catch(() => {
        enqueueSnackbar("devices.list.table.loadError", { variant: "error" });
      })
      .then(() => {
        setLoading(false);
        setUpdating(false);
      });
  }, [api, queryParams, readyToLoadDevices]);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        searchTerm ||
        typeFilter ||
        contentTypeFilter ||
        projectFilter.length > 0 ||
        ownerFilter.length > 0 ||
        statusFilter.length > 0
      ),
    [
      searchTerm,
      typeFilter,
      contentTypeFilter,
      projectFilter,
      ownerFilter,
      statusFilter
    ]
  );

  // memoize pagination component
  const paginationComponent = useMemo(
    () => (
      <Pagination
        totalCount={totalCount}
        perPage={perPage}
        page={page}
        onSelectPage={setPage}
        allowPerPageSelect={false}
      />
    ),
    [totalCount, perPage, page]
  );

  const pageSelectComponent = useMemo(
    () => (
      <PageSelect
        perPage={perPage}
        perPageOptions={[10, 25, 50, 100]}
        onSelectPerPage={handleSelectPerPage}
      />
    ),
    [perPage, handleSelectPerPage]
  );

  const tableComponent = useMemo(() => {
    if (devices.length === 0) {
      return <EmptyState hasFilters={hasActiveFilters} />;
    }

    return (
      <Table
        devices={devices}
        sort={sort}
        onUpdateSort={handleUpdateSort}
        groupByUser={groupByUser}
        isUpdating={isUpdating}
        tableColumns={tableColumns}
        sensorSettings={sensorSettings}
        onTogglePercentages={() =>
          handleShowPercentages(!sensorSettings.showPercentages)
        }
      />
    );
  }, [
    devices,
    hasActiveFilters,
    sort,
    handleUpdateSort,
    groupByUser,
    isUpdating,
    tableColumns,
    sensorSettings
  ]);

  const searchComponent = useMemo(
    () => (
      <SearchTermFilter
        onSetSearchTerm={handleSetSearchTerm}
        onSetTypeFilter={handleSetTypeFilter}
        onSetContentTypeFilter={handleSetContentTypeFilter}
        onSetProjectFilter={handleSetProjectFilter}
        onSetOwnerFilter={handleSetOwnerFilter}
        onSetStatusFilter={handleSetStatusFilter}
        initialFilters={{
          searchTerm: initialFilters.searchTerm,
          typeFilter: initialFilters.typeFilter || "all",
          medium: initialFilters.contentTypeFilter,
          project: initialFilters.projectFilter,
          ownerId: initialFilters.ownerFilter,
          status: initialFilters.statusFilter
        }}
      />
    ),
    // initialFilters is only used to hydrate SearchTermFilter's own state on
    // its first render, so it's intentionally left out of this memo's deps
    [
      handleSetSearchTerm,
      handleSetTypeFilter,
      handleSetContentTypeFilter,
      handleSetProjectFilter,
      handleSetOwnerFilter,
      handleSetStatusFilter
    ]
  );

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Switch>
        <RoleProtectedRoute role={ROLES.ADMIN} path={`${url}/new`}>
          <Create onClose={() => history.push("/devices")} />
        </RoleProtectedRoute>
        <Route path={`${url}/config`}>
          <ConfigDialog onClose={() => history.push("/devices")} />
        </Route>
        <Route path={`${url}/export`}>
          <Export onClose={() => history.push("/devices")} />
        </Route>
        <Route path={`${url}/block-queries`}>
          <BlockQueries onClose={() => history.push("/devices")} />
        </Route>
        <RoleProtectedRoute role={ROLES.USER} path={`${url}/ref-date-queries`}>
          <RefDateQueries onClose={() => history.push("/devices")} />
        </RoleProtectedRoute>
        <Route>
          <Redirect to={{ pathname: url, search: location.search }} />
        </Route>
      </Switch>

      <List
        onShowExport={() => history.push("/devices/export")}
        onShowConfig={() => history.push("/devices/config")}
        onShowBlockQueries={() => history.push("/devices/block-queries")}
        onShowRefDateQueries={() => history.push("/devices/ref-date-queries")}
        onShowCreate={() => history.push("/devices/new")}
        count={devices.length}
        totalCount={totalCount}
        paginationComponent={paginationComponent}
        pageSelectComponent={pageSelectComponent}
        tableComponent={tableComponent}
        searchComponent={searchComponent}
      />
    </>
  );
};

export default ListContainer;
